// Contracts — work.weern.com MANAGER views (read-only). Back the internal
// cabinet "photographers" section: overall stats, per-photographer counts, and
// drill-in to a photographer's uploads + per-batch review (accept/reject).
// Served by weern-work-backend, gated by a Firebase email allowlist
// (WORK_MANAGER_EMAILS) — NOT the photographer auth and NOT the static
// X-Admin-Secret. Shapes snapped from migrations/0001_init + 0004_manager_review.
import { z } from 'zod';
import { WorkCategorySchema, WorkCapabilitySchema } from './photographer';

export const WorkManagerByCategorySchema = z.object({
  tickets: z.number().int().nonnegative(),
  coupons: z.number().int().nonnegative(),
  giftcards: z.number().int().nonnegative(),
  // receipts + membership (2026-08-24) — .default(0) so a worker deployed
  // before the split still parses in the manager UI.
  receipts: z.number().int().nonnegative().default(0),
  membership: z.number().int().nonnegative().default(0),
});
export type WorkManagerByCategory = z.infer<typeof WorkManagerByCategorySchema>;

// Per-photographer payout config (managers set it via _admin/provision). null
// when no rate row exists.
export const WorkManagerRateSchema = z.object({
  amount: z.number(),
  currency: z.string(),
});
export type WorkManagerRate = z.infer<typeof WorkManagerRateSchema>;

// GET /api/work/manager/photographers — every photographer + aggregate stats.
export const WorkManagerPhotographerStatsSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string(),
  active: z.boolean(),
  bound: z.boolean(),               // uid claimed (the photographer has signed in)
  created_at: z.string(),
  total: z.number().int().nonnegative(),
  today: z.number().int().nonnegative(),
  by_category: WorkManagerByCategorySchema,
  flagged: z.number().int().nonnegative(),
  last_upload_at: z.string(),       // '' when they've uploaded nothing
  oldest_pending_at: z.string().default(''),   // created_at of their oldest UNREVIEWED frame; '' = nothing pending. Mobile sorts oldest-first.
  quality_pct: z.number(),          // lifetime accepted/(accepted+retakes); 100 if none
  batches: z.number().int().nonnegative(),   // distinct (session_id,bg_batch) groups
  done: z.number().int().nonnegative(),      // batches the manager has reviewed
  rate: WorkManagerRateSchema.nullable(),
  earned: z.number().nullable(),    // rate.amount * total, or null when no rate
});
export type WorkManagerPhotographerStats = z.infer<typeof WorkManagerPhotographerStatsSchema>;

export const WorkManagerPhotographersResSchema = z.object({
  totals: z.object({
    photographers: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    total_photos: z.number().int().nonnegative(),
    today_photos: z.number().int().nonnegative(),
    approved_pending: z.number().int().nonnegative(),   // accepted, non-rejected, not yet exported ("готово / к отгрузке")
    awaiting_qa: z.number().int().nonnegative().default(0),   // shot, not yet reviewed (review='') — "на QA"
    in_shooting: z.number().int().nonnegative().default(0),   // assigned to a photographer, not yet shot — "идёт съёмка"
  }),
  show_earnings: z.boolean(),
  photographers: z.array(WorkManagerPhotographerStatsSchema),
});
export type WorkManagerPhotographersRes = z.infer<typeof WorkManagerPhotographersResSchema>;

// GET /api/work/manager/photographer?id=<photographer_id> — one photographer's
// uploads, batched by (session_id, bg_batch) newest first, with signed thumb +
// full capability URLs (no Firebase token in the URL). Mirrors /history but for
// any photographer, manager-gated, and exposes more per-photo metadata.
export const WorkManagerPhotoSchema = z.object({
  id: z.string(),
  position: z.number().int(),
  category: WorkCategorySchema,
  has_code: z.boolean(),
  status: z.string(),               // 'accepted' | 'flagged'
  width: z.number().int(),
  height: z.number().int(),
  blur_score: z.number(),
  created_at: z.string(),
  rejected: z.boolean(),            // per-frame reject flag (work_photos.review)
  downloaded: z.boolean(),          // exported/archived (work_photos.downloaded_at)
  thumb_url: z.string(),
  full_url: z.string(),
});
export type WorkManagerPhoto = z.infer<typeof WorkManagerPhotoSchema>;

// review = null while the batch is pending; set once the manager resolves it.
export const WorkManagerBatchReviewSchema = z.object({
  status: z.enum(['accepted', 'rejected']),
  comment: z.string(),
  reviewed_at: z.string(),
});
export type WorkManagerBatchReview = z.infer<typeof WorkManagerBatchReviewSchema>;

export const WorkManagerBatchSchema = z.object({
  session_id: z.string(),
  bg_batch: z.number().int(),
  category: WorkCategorySchema,
  count: z.number().int().nonnegative(),
  first_at: z.string(),
  last_at: z.string(),
  review: WorkManagerBatchReviewSchema.nullable(),
  photos: z.array(WorkManagerPhotoSchema),
  // Cron background-similarity verdict for the batch (migrations/0026):
  // ''=not checked yet | same | different (flagged to manual) | unclear | error.
  bg_check: z.string().default(''),
});
export type WorkManagerBatch = z.infer<typeof WorkManagerBatchSchema>;

// ─── Reject report (W7): «что не принято и почему» одним кликом ──────────────
export const WorkManagerRejectReportReqSchema = z.object({
  id: z.string().trim().min(1).max(64),          // photographer_id
  days: z.number().int().positive().max(90).default(14),
  // 'ru' для внутреннего разбора, 'en' — отправить контрактору как есть.
  lang: z.enum(['ru', 'en']).default('en'),
});
export type WorkManagerRejectReportReq = z.infer<typeof WorkManagerRejectReportReqSchema>;

export const WorkManagerRejectReportResSchema = z.object({
  report: z.string(),                            // готовый текст для копирования
  llm_polished: z.boolean(),                     // false → детерминированный шаблон
  totals: z.object({
    uploaded: z.number().int().nonnegative(),
    credited: z.number().int().nonnegative(),
    review: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
  }),
  reasons: z.array(z.object({ reason: z.string(), n: z.number().int().nonnegative() })),
});
export type WorkManagerRejectReportRes = z.infer<typeof WorkManagerRejectReportResSchema>;

export const WorkManagerPhotographerDetailResSchema = z.object({
  photographer: z.object({
    id: z.string(),
    email: z.string(),
    display_name: z.string(),
    active: z.boolean(),
    bound: z.boolean(),
    created_at: z.string(),
  }),
  stats: z.object({
    total: z.number().int().nonnegative(),
    today: z.number().int().nonnegative(),
    by_category: WorkManagerByCategorySchema,
    flagged: z.number().int().nonnegative(),
    last_upload_at: z.string(),
    quality_pct: z.number(),
  }),
  rate: WorkManagerRateSchema.nullable(),
  earned: z.number().nullable(),
  show_earnings: z.boolean(),
  batches: z.array(WorkManagerBatchSchema),
});
export type WorkManagerPhotographerDetailRes = z.infer<typeof WorkManagerPhotographerDetailResSchema>;

// Query for the detail endpoint (the photographer's stable id).
export const WorkManagerPhotographerQuerySchema = z.object({
  id: z.string().trim().min(1),
});
export type WorkManagerPhotographerQuery = z.infer<typeof WorkManagerPhotographerQuerySchema>;

// POST /api/work/manager/reviewBatch — accept or reject one batch.
// accepted: ships every frame EXCEPT rejected_photo_ids (those get review=
// 'rejected'); rejected: flags the whole batch. comment travels back to the
// photographer. Idempotent — re-reviewing overwrites the prior decision.
export const WorkReviewBatchReqSchema = z.object({
  photographer_id: z.string().trim().min(1),
  session_id: z.string().trim().min(1).max(64),
  bg_batch: z.number().int().nonnegative(),
  status: z.enum(['accepted', 'rejected']),
  comment: z.string().trim().max(2000).default(''),
  rejected_photo_ids: z.array(z.string().trim().min(1)).max(50).default([]),
});
export type WorkReviewBatchReq = z.infer<typeof WorkReviewBatchReqSchema>;

// POST /api/work/manager/reopenBatch — drop the review, return the batch to
// pending (clears per-frame rejects too).
export const WorkReopenBatchReqSchema = z.object({
  photographer_id: z.string().trim().min(1),
  session_id: z.string().trim().min(1).max(64),
  bg_batch: z.number().int().nonnegative(),
});
export type WorkReopenBatchReq = z.infer<typeof WorkReopenBatchReqSchema>;

export const WorkReviewResSchema = z.object({ ok: z.boolean() });
export type WorkReviewRes = z.infer<typeof WorkReviewResSchema>;

// GET /api/work/manager/approved — every approved (batch accepted, not per-frame
// rejected) photo that has NOT been exported yet, with full D1 metadata + a
// signed full-image URL. Capped; `count`/`capped` tell the client if more remain.
export const WorkApprovedPhotoSchema = z.object({
  id: z.string(),
  photographer_id: z.string(),
  photographer_name: z.string(),
  photographer_email: z.string(),
  category: WorkCategorySchema,
  session_id: z.string(),
  bg_batch: z.number().int(),       // «фон»
  position: z.number().int(),       // порядковый номер в серии
  r2_key: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  blur_score: z.number(),
  has_code: z.boolean(),
  code_value: z.string(),
  codes: z.string(),                // GROUP_CONCAT of identifier codes (work_codes)
  status: z.string(),
  created_at: z.string(),
  batch_comment: z.string(),
  reviewed_at: z.string(),
  full_url: z.string(),
});
export type WorkApprovedPhoto = z.infer<typeof WorkApprovedPhotoSchema>;

export const WorkApprovedResSchema = z.object({
  photos: z.array(WorkApprovedPhotoSchema),
  count: z.number().int().nonnegative(),     // photos in THIS pull (≤ cap)
  total_pending: z.number().int().nonnegative(),
  capped: z.boolean(),                       // true if more remain beyond this pull
});
export type WorkApprovedRes = z.infer<typeof WorkApprovedResSchema>;

// POST /api/work/manager/markDownloaded — archive the exported photos so they
// don't come back in the next approved pull.
export const WorkMarkDownloadedReqSchema = z.object({
  photo_ids: z.array(z.string().trim().min(1)).min(1).max(2000),
});
export type WorkMarkDownloadedReq = z.infer<typeof WorkMarkDownloadedReqSchema>;

export const WorkMarkDownloadedResSchema = z.object({ ok: z.boolean(), marked: z.number().int().nonnegative() });
export type WorkMarkDownloadedRes = z.infer<typeof WorkMarkDownloadedResSchema>;

// POST /api/work/manager/setCapabilities — a manager sets the FULL capability set
// for a contractor (replace semantics: the listed set becomes the new grant).
// Backs the «Контракторы» capability toggles. Empty array = revoke everything.
export const WorkSetCapabilitiesReqSchema = z.object({
  photographer_id: z.string().trim().min(1),
  capabilities: z.array(WorkCapabilitySchema).max(16),
});
export type WorkSetCapabilitiesReq = z.infer<typeof WorkSetCapabilitiesReqSchema>;

export const WorkSetCapabilitiesResSchema = z.object({
  ok: z.boolean(),
  capabilities: z.array(WorkCapabilitySchema),
});
export type WorkSetCapabilitiesRes = z.infer<typeof WorkSetCapabilitiesResSchema>;

// ─── Manager «Нерассортированные» staging pool ────────────────────────────────
// A photographer sometimes hands the manager loose photos out-of-band. The
// manager uploads a ZIP into a SPECIFIC photographer; each image stages here
// (pre-ingest, no code, INVISIBLE to the photographer — kept OUT of work_photos,
// see migrations/0014). The manager visually groups ≤5 into a background;
// «Создать фон» runs each through the normal freeform ingest (GCP decode →
// work_codes dedup) so only NEW codes reach the deliverable. Junk is hard-
// deleted (row + R2). Manager-gated like the rest of /api/work/manager/*.

// POST /api/work/manager/uploadUnsorted — one staged image (base64 full + thumb).
export const WorkUploadUnsortedReqSchema = z.object({
  photographer_id: z.string().trim().min(1),
  category: WorkCategorySchema,
  fileBase64: z.string().min(1),
  thumbBase64: z.string().min(1),
  mime: z.string().trim().min(1).max(64),
  width: z.number().int().nonnegative().default(0),
  height: z.number().int().nonnegative().default(0),
  orig_name: z.string().trim().max(260).default(''),
});
export type WorkUploadUnsortedReq = z.infer<typeof WorkUploadUnsortedReqSchema>;

export const WorkUploadUnsortedResSchema = z.object({ ok: z.boolean(), id: z.string() });
export type WorkUploadUnsortedRes = z.infer<typeof WorkUploadUnsortedResSchema>;

// GET /api/work/manager/unsorted?id=<photographer_id> — the staged pool.
export const WorkUnsortedQuerySchema = z.object({ id: z.string().trim().min(1) });
export type WorkUnsortedQuery = z.infer<typeof WorkUnsortedQuerySchema>;

// status: 'staged' (in pool, sortable) | 'queued'/'processing' (assigned to a
// фон, decoding in the background) | 'duplicate'/'no_code' (decoded, rejected →
// shown in the bottom grid).
export const WorkUnsortedItemSchema = z.object({
  id: z.string(),
  category: WorkCategorySchema,
  status: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  orig_name: z.string(),
  created_at: z.string(),
  thumb_url: z.string(),
  full_url: z.string(),
});
export type WorkUnsortedItem = z.infer<typeof WorkUnsortedItemSchema>;

export const WorkUnsortedResSchema = z.object({
  photos: z.array(WorkUnsortedItemSchema),
  count: z.number().int().nonnegative(),
  queued: z.number().int().nonnegative().default(0),   // still decoding (queued+processing) → client polls while > 0
});
export type WorkUnsortedRes = z.infer<typeof WorkUnsortedResSchema>;

// POST /api/work/manager/createBackground — assign ≤5 staged photos to ONE new
// background and ENQUEUE them for background decode (instant return; the manager
// doesn't wait). A background worker decodes + dedups each: new code → moves to
// «На проверке»; duplicate / no code → stays in the pool with that status (bottom
// grid). Durable: D1 holds the queue, so it survives page refresh / worker death.
export const WorkCreateBackgroundReqSchema = z.object({
  photographer_id: z.string().trim().min(1),
  ids: z.array(z.string().trim().min(1)).min(1).max(5),
});
export type WorkCreateBackgroundReq = z.infer<typeof WorkCreateBackgroundReqSchema>;

export const WorkCreateBackgroundResSchema = z.object({
  ok: z.boolean(),
  session_id: z.string(),
  queued: z.number().int().nonnegative(),   // how many were enqueued for decode
});
export type WorkCreateBackgroundRes = z.infer<typeof WorkCreateBackgroundResSchema>;

// POST /api/work/manager/deleteUnsorted — hard-delete staged photos (row + R2).
export const WorkDeleteUnsortedReqSchema = z.object({
  photographer_id: z.string().trim().min(1),
  ids: z.array(z.string().trim().min(1)).min(1).max(200),
});
export type WorkDeleteUnsortedReq = z.infer<typeof WorkDeleteUnsortedReqSchema>;

export const WorkDeleteUnsortedResSchema = z.object({ ok: z.boolean(), deleted: z.number().int().nonnegative() });
export type WorkDeleteUnsortedRes = z.infer<typeof WorkDeleteUnsortedResSchema>;

export const WorkManagerPrintBatchRowSchema = z.object({
  photographer_id: z.string(),
  display_name: z.string(),
  email: z.string(),
  category: z.string(),
  day: z.string(),
  sent: z.number().int(),
  captured: z.number().int(),
  first_captured_at: z.string(),
  last_captured_at: z.string(),
});
export type WorkManagerPrintBatchRow = z.infer<typeof WorkManagerPrintBatchRowSchema>;

export const WorkManagerPrintBatchesResSchema = z.object({
  rows: z.array(WorkManagerPrintBatchRowSchema),
});
export type WorkManagerPrintBatchesRes = z.infer<typeof WorkManagerPrintBatchesResSchema>;

export const WorkReviewFrameReqSchema = z.object({
  // 128, не 64: work-dev-мост шлёт детерминированный id `wd-<card_id>-<stem>`
  // (devoutputs.ts, слайс на 128) — фотографские кадры (crypto.randomUUID(),
  // 36 симв.) укладываются с большим запасом.
  photo_id: z.string().trim().min(1).max(128),
  verdict: z.enum(['accepted', 'rejected']),
  comment: z.string().max(500).default(''),
});
export type WorkReviewFrameReq = z.infer<typeof WorkReviewFrameReqSchema>;
export const WorkReviewFrameResSchema = z.object({ ok: z.boolean() });
export type WorkReviewFrameRes = z.infer<typeof WorkReviewFrameResSchema>;
